import {
  _INPUT_SALE_DEPOTS,
  _INPUT_SALE_REGROUPING,
  _INPUT_SALE_TURN_OVER,
  _INPUT_SALES_OTHERS,
  _SALE_INPUTS,
  _TAB_INPUT_SALES,
} from "../../../utils/constants";

/**
 * Calculates the value for a specific month based on the provided parameters, tab, and discount increases.
 *
 * @param {Object} params - The parameters object containing data and column definitions.
 * @param {Object} params.data - The data object for the current row.
 * @param {Object} params.colDef - The column definition object.
 * @param {string} params.colDef.field - The field name of the column.
 * @param {Object} params.api - The grid API for accessing and iterating over nodes.
 * @param {string} tab - The tab type, which determines the calculation logic.
 * @param {number} [discountIncreases] - The discount or increase percentage to apply in calculations.
 * @returns {number} - The calculated value for the specified month.
 */
export const monthsCalculvalueGetter = (params, tab, discountIncreases) => {
  const creditCalcul = [
    _INPUT_SALE_REGROUPING.EXTERNAL_CREDIT_GROUPING,
    _INPUT_SALE_REGROUPING.GCC_GROUPING,
    _INPUT_SALE_REGROUPING.OTHER_GROUPING,
    _INPUT_SALE_REGROUPING.UNSECURED_GROUPING,
    _INPUT_SALE_REGROUPING.BOLCK_GROUPING,
    _INPUT_SALE_REGROUPING.DEPOT_GROUPING,
  ];
  const creditCalculDiscount = [
    _INPUT_SALE_REGROUPING.PMX_GROUPING,
    _INPUT_SALE_REGROUPING.DMX_GROUPING,
  ];
  const data = params.data;

  if (!data) return 0;

  const fiscalMonthIndex = params.colDef.field;
  const month = fiscalMonthIndex.split(".")[1];

  if (tab !== _TAB_INPUT_SALES.VALUE) return data.months[month];

  // Calculating Total External + Internal Volume
  if (
    data.type === _INPUT_SALE_REGROUPING.TOTAL_GROUPING &&
    data.sale === _INPUT_SALES_OTHERS.TOTAL_EXTERNAL_INTERNAL
  ) {
    const types = [
      _INPUT_SALE_REGROUPING.EXTERNAL_CASH_GROUPING,
      _INPUT_SALE_REGROUPING.EXTERNAL_CREDIT_GROUPING,
      _INPUT_SALE_REGROUPING.BOLCK_GROUPING,
      _INPUT_SALE_REGROUPING.UNSECURED_GROUPING,
    ];

    const inputs = [
      _SALE_INPUTS.aggregates,
      _SALE_INPUTS.crusherRun,
      _SALE_INPUTS.rocksand,
      _SALE_INPUTS.spallsFilterBoulders,
      _INPUT_SALES_OTHERS.INTERNAL_BLOCK,
      _INPUT_SALE_REGROUPING.UNSECURED_GROUPING,
    ];

    let total = 0;
    params.api.forEachLeafNode((node) => {
      if (types.includes(node.data.type) && inputs.includes(node.data.sale)) {
        total += parseFloat(node.data.calculs[month] || 0);
      }
    });

    return (data.months[month] = data.calculs[month] = total);
  }

  // Calculating tonnage with cash
  if (
    data.type === _INPUT_SALE_REGROUPING.EXTERNAL_CASH_GROUPING &&
    data.cash !== undefined &&
    data.cash !== 0 &&
    discountIncreases !== undefined
  ) {
    // Toujours recalculer (pas de cache)
    data.calculs[month] = valueCalculwithoutDiscount(
      data.months[month],
      data.cash,
      discountIncreases,
      parseInt(month),
    );

    return data.calculs[month];
  }

  // Calculating tonnage with credit
  if (data.credit !== undefined) {
    return discountCalcul(
      creditCalculDiscount,
      creditCalcul,
      params,
      params.data,
      month,
      discountIncreases,
    );
  }

  //external credit tonnage calculation
  if (data.type === _INPUT_SALE_REGROUPING.EXTERNAL_CREDIT_GROUPING) {
    const regroups = [
      _INPUT_SALE_REGROUPING.PMX_GROUPING,
      _INPUT_SALE_REGROUPING.DMX_GROUPING,
      _INPUT_SALE_REGROUPING.GCC_GROUPING,
      _INPUT_SALE_REGROUPING.OTHER_GROUPING,
    ];
    let totalValue = 0;
    params.api.forEachLeafNode((node) => {
      if (regroups.includes(node.data.type) && node.data.sale === data.sale) {
        totalValue += discountCalcul(
          creditCalculDiscount,
          creditCalcul,
          params,
          node.data,
          month,
        );
      }
    });

    // Toujours recalculer (pas de cache)
    data.calculs[month] = totalValue;

    return totalValue;
  }

  // Calculating TURN OVER
  if (
    data.type === _INPUT_SALE_REGROUPING.TURN_GROUPING &&
    data.sale === _INPUT_SALE_TURN_OVER.EXTERNAL_TURN_OVER
  ) {
    const types = [
      _INPUT_SALE_REGROUPING.EXTERNAL_CASH_GROUPING,
      _INPUT_SALE_REGROUPING.EXTERNAL_CREDIT_GROUPING,
      _INPUT_SALE_REGROUPING.PRODUCT_GROUPING,
      _INPUT_SALE_REGROUPING.UNSECURED_GROUPING,
    ];
    const inputs = [
      _SALE_INPUTS.aggregates,
      _SALE_INPUTS.crusherRun,
      _SALE_INPUTS.rocksand,
      _SALE_INPUTS.spallsFilterBoulders,
      _INPUT_SALE_REGROUPING.UNSECURED_GROUPING,
      _INPUT_SALES_OTHERS.BY_PRODUCT_RS,
    ];

    // line EXTERNAL TURN OVER
    let total = 0;
    params.api.forEachLeafNode((node) => {
      if (types.includes(node.data.type) && inputs.includes(node.data.sale)) {
        const nodeCalcul = parseFloat(node.data.calculs[month] || 0);
        const nodeMonth =
          node.data.sale === _INPUT_SALES_OTHERS.BY_PRODUCT_RS
            ? parseFloat(node.data.months[month] || 0)
            : 0;
        total += nodeCalcul + nodeMonth;
      }
    });
    data.months[month] = data.calculs[month] = total;

    return total;
  }

  if (
    data.type === _INPUT_SALE_REGROUPING.TURN_GROUPING &&
    data.sale === _INPUT_SALE_TURN_OVER.INTERNAL_TURN_OVER
  ) {
    const types = [
      _INPUT_SALE_REGROUPING.BOLCK_GROUPING,
      _INPUT_SALE_REGROUPING.DEPOT_GROUPING,
    ];
    const inputs = [
      _INPUT_SALE_DEPOTS.TERRE_ROUGE,
      _INPUT_SALE_DEPOTS.VALENTINA,
      _INPUT_SALES_OTHERS.INTERNAL_BLOCK,
    ];

    // line INTERNAL TURN OVER
    let total = 0;
    params.api.forEachLeafNode((node) => {
      if (types.includes(node.data.type) && inputs.includes(node.data.sale)) {
        total += parseFloat(node.data.calculs[month] || 0);
      }
    });

    return (data.months[month] = data.calculs[month] = total);
  }

  return data.months[month];
};

/**
 * Calculates the discount value for a given data entry based on various conditions.
 *
 * @param {Array<string>} creditCalculDiscount - An array of data types that are subject to discounts.
 * @param {Array<string>} creditCalcul - An array of data types that are not subject to discounts.
 * @param {Object} params - Parameters object, typically containing the API for accessing grid data.
 * @param {Object} data - The data entry being processed, containing details such as type, sale, months, and credit.
 * @param {string} month - The month for which the calculation is being performed.
 * @param {Array<number>} discountIncreases - An array of discount increase values used in calculations.
 * @returns {number} - The calculated value for the given month, considering discounts and other conditions.
 */
const discountCalcul = (
  creditCalculDiscount,
  creditCalcul,
  params,
  data,
  month,
  discountIncreases,
) => {
  let discountMonth = undefined;
  params.api.forEachLeafNode((node) => {
    if (node.data.type === data.type && node.data.sale.includes("Discount")) {
      if (discountMonth === undefined || discountMonth === 0) {
        discountMonth =
          node.data.months[month] === undefined ||
          node.data.months[month] === null
            ? 0
            : parseFloat(node.data.months[month]);
      }
    }
  });

  // discount lines are not taken into account
  if (data.sale.includes("Discount")) {
    return data.months[month];
  }

  if (
    data.months[month] === null ||
    data.credit === null ||
    data.months[month] === 0 ||
    data.credit === 0
  ) {
    return data.months[month];
  }

  // cells subject to a discount
  if (creditCalculDiscount.includes(data.type)) {
    if (discountMonth !== undefined) {
      // Toujours recalculer (pas de cache)
      data.calculs[month] =
        data.months[month] * data.credit * (1 - discountMonth / 100);

      return data.calculs[month];
    }
    // Toujours recalculer (pas de cache)
    data.calculs[month] = data.months[month] * data.credit;

    return data.calculs[month];
  }

  // cells without discount
  if (
    creditCalcul.includes(data.type) &&
    data.months[month] !== 0 &&
    data.credit !== 0
  ) {
    // Toujours recalculer (pas de cache)
    data.calculs[month] =
      data.type !== _INPUT_SALE_REGROUPING.UNSECURED_GROUPING
        ? valueCalculwithoutDiscount(
            data.months[month],
            data.credit,
            discountIncreases,
            month,
          )
        : data.months[month] * data.credit;

    return data.calculs[month];
  }

  return data.months[month];
};

/**
 * Calculates the value without discount for a given month based on tonnage, coefficient, and discount increases.
 *
 * @param {number} tonnage - The tonnage value to be used in the calculation.
 * @param {number} coef - The coefficient to be applied in the calculation.
 * @param {Object} discountIncreases - An object containing discount increase percentages for each month.
 * @param {string} month - The month for which the calculation is performed.
 * @returns {number} - The calculated value without discount for the specified month.
 */
const valueCalculwithoutDiscount = (
  tonnage,
  coef,
  discountIncreases,
  month,
) => {
  const discountIncrease =
    discountIncreases !== undefined && discountIncreases[[month]] !== undefined
      ? discountIncreases[[month]]
      : 0;

  return tonnage * coef * (1 + discountIncrease / 100);
};
